/* DocScrubs Studio — Shopify storefront interactions. No external dependencies. */
(() => {
  'use strict';
  const cfg = window.DS || {};
  const cartEndpoint = (action) => `${(cfg.root || '/').replace(/\/$/, '')}/cart/${action}.js`;
  const updateUrl = cfg.update || cartEndpoint('update');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => reduced.matches ? 'instant' : 'smooth';
  const announce = (message) => { const el = document.getElementById('StoreStatus'); if (el) el.textContent = message; };
  const errorText = (data) => typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : cfg.error;
  const showError = (el, message) => { if (el) { el.textContent = message || cfg.error; el.hidden = false; } };
  const fetchJSON = async (url, init = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', ...init, headers: { Accept: 'application/json', ...init.headers } });
    const raw = await response.text();
    let data = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch (_) { data = { description: raw || cfg.error }; }
    if (!response.ok || Number(data.status) >= 400) throw new Error(errorText(data) || `Request failed (${response.status})`);
    return data;
  };
  let previousFocus;
  function openDialog(dialog, trigger) {
    if (!dialog?.showModal) return false;
    previousFocus = trigger || document.activeElement;
    if (!dialog.open) dialog.showModal();
    return true;
  }
  function closeDialog(dialog) {
    dialog?.close();
    if (previousFocus?.isConnected) previousFocus.focus();
  }
  function applyCart(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-fragment]');
    const current = document.querySelector('#CartDrawer [data-cart-fragment]');
    if (!fresh || !current) return false;
    const focused = document.activeElement;
    const key = focused?.dataset.lineKey;
    const wasQuantity = focused?.matches('[data-cart-quantity]');
    current.replaceWith(fresh);
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = fresh.dataset.count; });
    if (wasQuantity && key) {
      const next = Array.from(fresh.querySelectorAll('[data-cart-quantity]')).find(el => el.dataset.lineKey === key);
      (next || document.querySelector('#CartDrawer [data-dialog-close]'))?.focus();
    }
    return true;
  }
  async function refreshCart() {
    const url = new URL(cfg.cart, location.origin);
    url.searchParams.set('sections', 'cart-drawer');
    const data = await fetchJSON(url.toString());
    if (!applyCart(data['cart-drawer'])) throw new Error(cfg.error);
  }
  let cartQueue = Promise.resolve();
  function queueCart(task) {
    const result = cartQueue.then(task);
    cartQueue = result.catch(() => {});
    return result;
  }
  function drawerBusy(value) {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    drawer.setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-cart-quantity],button[name="checkout"]').forEach(el => { el.disabled = value; });
  }
  function bundleLineKeys(bundleId) {
    if (!bundleId) return [];
    return Array.from(document.querySelectorAll('[data-cart-line]'))
      .filter(line => line.dataset.bundleId === bundleId && line.dataset.lineKey)
      .map(line => line.dataset.lineKey)
      .filter((key, index, all) => all.indexOf(key) === index);
  }
  async function changeCart(key, quantity, bundleId = '') {
    return queueCart(async () => {
      const error = document.querySelector('[data-cart-error]');
      if (error) error.hidden = true;
      drawerBusy(true);
      try {
        let data;
        const keys = bundleLineKeys(bundleId);
        if (bundleId && keys.length > 1) {
          const updates = {};
          keys.forEach(lineKey => { updates[lineKey] = quantity; });
          data = await fetchJSON(updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates, sections: ['cart-drawer'], sections_url: location.pathname })
          });
        } else {
          data = await fetchJSON(cfg.change, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: location.pathname })
          });
        }
        if (!applyCart(data.sections?.['cart-drawer'])) await refreshCart();
      } catch (err) {
        try { await refreshCart(); } catch (_) { /* Keep the last displayed cart. */ }
        showError(error, err.message);
      } finally { drawerBusy(false); }
    });
  }
  async function removePageCartLine(key, bundleId = '') {
    return queueCart(async () => {
      const keys = bundleLineKeys(bundleId);
      if (bundleId && keys.length > 1) {
        const updates = {};
        keys.forEach(lineKey => { updates[lineKey] = 0; });
        await fetchJSON(updateUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      } else {
        await fetchJSON(cfg.change, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) });
      }
      location.reload();
    });
  }

  async function clearCart(context = 'drawer') {
    return queueCart(async () => {
      const error = document.querySelector('[data-cart-error]');
      if (error) error.hidden = true;
      const clearUrl = cartEndpoint('clear');
      if (context !== 'page') drawerBusy(true);
      try {
        await fetchJSON(clearUrl, { method: 'POST' });
        announce('Cart cleared');
        if (context === 'page') {
          location.assign(cfg.cart);
          return;
        }
        await refreshCart();
      } catch (err) {
        showError(error, err.message);
      } finally {
        if (context !== 'page') drawerBusy(false);
      }
    });
  }

  function removeCustomizationProperty(properties, label) {
    const props = { ...(properties || {}) };
    const normalized = String(label || '').toLowerCase();
    if (normalized.startsWith('name embroidery')) delete props['Embroidery Name'];
    else if (normalized.startsWith('logo embroidery')) delete props['Logo Choice'];
    else if (normalized.startsWith('badge name')) delete props['Badge Name'];
    else if (normalized.startsWith('badge logo')) delete props['Badge Logo'];
    return props;
  }

  async function removeAddonLine(key, bundleId = '', label = '', context = 'drawer') {
    return queueCart(async () => {
      const error = document.querySelector('[data-cart-error]');
      if (error) error.hidden = true;
      if (context !== 'page') drawerBusy(true);
      try {
        await fetchJSON(cfg.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        if (bundleId) {
          const cart = await fetchJSON(`${cfg.cart}.js`);
          const bundleLines = (cart.items || []).filter(item => item.properties?._bundle_id === bundleId);
          const baseLine = bundleLines.find(item => !item.properties?._addon_for);
          const remainingAddons = bundleLines.filter(item => item.properties?._addon_for);
          if (baseLine) {
            const props = removeCustomizationProperty(baseLine.properties, label);
            const remainingLabels = remainingAddons.map(item => item.properties?._addon_label).filter(Boolean);
            if (remainingLabels.length) props.Customization = remainingLabels.join(' + ');
            else {
              delete props.Customization;
              delete props._bundle_id;
            }
            await fetchJSON(cfg.change, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: baseLine.key, quantity: baseLine.quantity, properties: props })
            });
          }
        }

        announce('Customization removed');
        if (context === 'page') location.reload();
        else await refreshCart();
      } catch (err) {
        try { if (context !== 'page') await refreshCart(); } catch (_) {}
        showError(error, err.message);
      } finally {
        if (context !== 'page') drawerBusy(false);
      }
    });
  }

  const isChecked = (root, selector) => !!root?.querySelector(selector)?.checked;
  const money = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'PKR', maximumFractionDigits: 0 }).format(amount); }
    catch (_) { return `Rs. ${amount.toLocaleString()}`; }
  };

  function customizationState(root) {
    if (!root) return { properties: {}, addons: [], extra: 0, errors: [] };
    const properties = {};
    const addons = [];
    const summary = [];
    const errors = [];
    let extra = 0;

    const add = (key, label, idKey, priceKey) => {
      const id = Number(root.dataset[idKey] || 0);
      const price = Number(root.dataset[priceKey] || 0);
      addons.push({ key, label, id, price });
      extra += price;
      summary.push(label);
    };

    if (isChecked(root, '[data-addon-toggle="name"]')) {
      const text = root.querySelector('[data-custom-name]')?.value?.trim() || '';
      if (!text) errors.push('Please enter the embroidery name.');
      else properties['Embroidery Name'] = text;
      add('name', 'Name Embroidery', 'addonNameId', 'addonNamePrice');
    }
    if (isChecked(root, '[data-addon-toggle="logo"]')) {
      const logo = root.querySelector('[data-logo-choice]:checked')?.value || '';
      if (!logo) errors.push('Please choose an embroidery logo.');
      else properties['Logo Choice'] = logo;
      add('logo', logo ? `Logo Embroidery (${logo})` : 'Logo Embroidery', 'addonLogoId', 'addonLogoPrice');
    }
    if (isChecked(root, '[data-addon-toggle="badge-name"]')) {
      const text = root.querySelector('[data-badge-name]')?.value?.trim() || '';
      if (!text) errors.push('Please enter the badge name.');
      else properties['Badge Name'] = text;
      add('badgeName', 'Badge Name', 'addonBadgeNameId', 'addonBadgeNamePrice');
    }
    if (isChecked(root, '[data-addon-toggle="badge-logo"]')) {
      properties['Badge Logo'] = 'Yes';
      add('badgeLogo', 'Badge Logo', 'addonBadgeLogoId', 'addonBadgeLogoPrice');
    }
    const note = root.querySelector('[data-custom-note]')?.value?.trim() || '';
    if (note) properties['Customization Order Note'] = note;
    if (summary.length) properties['Customization'] = summary.join(' + ');
    return { properties, addons, extra, errors };
  }

  function updateCustomization(root) {
    if (!root) return;

    ['embroidery', 'badge'].forEach(group => {
      const yes = root.querySelector(`[data-addon-master="${group}"][value="yes"]`);
      const fields = root.querySelector(`[data-addon-master-fields="${group}"]`);
      const enabled = !!yes?.checked;
      if (fields) fields.hidden = !enabled;
      if (!enabled && fields) {
        fields.querySelectorAll('[data-addon-toggle]').forEach(toggle => {
          toggle.checked = false;
          const key = toggle.dataset.addonToggle;
          const off = fields.querySelector(`[data-addon-off="${key}"]`);
          if (off) off.checked = true;
        });
      }
    });

    ['name', 'logo', 'badge-name'].forEach(key => {
      const toggle = root.querySelector(`[data-addon-toggle="${key}"]`);
      const detail = root.querySelector(`[data-addon-detail="${key}"]`);
      if (detail) detail.hidden = !toggle?.checked;
    });

    const embroideryMaster = root.querySelector('[data-addon-master="embroidery"][value="yes"]');
    const badgeMaster = root.querySelector('[data-addon-master="badge"][value="yes"]');
    const noteWrap = root.querySelector('[data-addon-note-wrap]');
    const anyMasterEnabled = !!embroideryMaster?.checked || !!badgeMaster?.checked;
    if (noteWrap) noteWrap.hidden = !anyMasterEnabled;
    if (!anyMasterEnabled) {
      const noteInput = root.querySelector('[data-custom-note]');
      if (noteInput) noteInput.value = '';
    }

    const product = root.closest('ds-product');
    const quantity = Math.max(1, Number(product?.querySelector('[data-product-quantity]')?.value || 1));
    const base = Number(product?.dataset.variantPrice || 0);
    const compare = Number(product?.dataset.variantCompareAtPrice || 0);
    const state = customizationState(root);
    const unitTotal = base + state.extra;

    const customTotal = root.querySelector('[data-customization-total]');
    const displayTotal = root.querySelector('[data-display-total]');
    if (customTotal) customTotal.textContent = money(state.extra * quantity);
    if (displayTotal) displayTotal.textContent = money(unitTotal * quantity);

    const currentPrice = product?.querySelector('[data-main-current-price]');
    const comparePrice = product?.querySelector('[data-main-compare-price]');
    const savings = product?.querySelector('[data-main-savings]');
    if (currentPrice) currentPrice.textContent = money(unitTotal);

    if (state.extra > 0) {
      if (comparePrice) comparePrice.hidden = true;
      if (savings) savings.hidden = true;
    } else {
      if (comparePrice) {
        comparePrice.hidden = !(compare > base);
        if (compare > base) comparePrice.textContent = money(compare);
      }
      if (savings) {
        if (compare > base) {
          const saved = compare - base;
          const percent = Math.round((saved * 100) / compare);
          savings.textContent = `You Save: ${money(saved)} (${percent}%)`;
          savings.hidden = false;
        } else {
          savings.hidden = true;
        }
      }
    }
  }

  function validateCustomization(root, errorEl) {
    if (!root) return true;
    const state = customizationState(root);
    if (state.errors.length) {
      showError(errorEl, state.errors[0]);
      return false;
    }
    if (errorEl) errorEl.hidden = true;
    return true;
  }

  async function resolveAddonIds(root) {
    if (!root) return;
    const required = [
      ['addonNameId', 'DS-ADDON-NAME', 'Name Embroidery'],
      ['addonLogoId', 'DS-ADDON-LOGO', 'Logo Embroidery'],
      ['addonBadgeNameId', 'DS-ADDON-BADGE-NAME', 'Badge Name'],
      ['addonBadgeLogoId', 'DS-ADDON-BADGE-LOGO', 'Badge Logo']
    ];
    if (required.every(([key]) => Number(root.dataset[key]))) return;
    const path = root.dataset.addonProductUrl || '/products/docscrubs-customization-addons.js';
    const url = new URL(path, location.origin);
    if (cfg.root && cfg.root !== '/') url.pathname = `${cfg.root.replace(/\/$/, '')}${url.pathname}`;
    const product = await fetchJSON(url.toString());
    const variants = Array.isArray(product.variants) ? product.variants : [];
    required.forEach(([key, sku, title]) => {
      const variant = variants.find(v => v.sku === sku) || variants.find(v => v.title === title);
      if (variant?.id) root.dataset[key] = String(variant.id);
    });
    if (!required.every(([key]) => Number(root.dataset[key]))) {
      throw new Error('Customization service variants are unavailable. Open the Customization product in Shopify and make sure it is active on the Online Store channel.');
    }
  }

  function makeBundleId() {
    if (window.crypto?.randomUUID) return `ds-${window.crypto.randomUUID()}`;
    return `ds-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async function addBundleItems(items) {
    if (!Array.isArray(items) || !items.length) throw new Error('No cart items were selected.');
    return fetchJSON(cfg.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
  }

  async function performProductAdd(form) {
    if (!form.matches('[data-addon-bundle-form]')) {
      return fetchJSON(cfg.add, { method: 'POST', body: new FormData(form) });
    }
    const product = form.closest('ds-product');
    const root = product?.querySelector('[data-addon-customization]');
    const errorEl = product?.querySelector('[data-variant-error]');
    const id = Number(form.querySelector('[name="id"]')?.value || 0);
    const quantity = Math.max(1, parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1);
    if (!id) throw new Error('Please select an available color and size.');
    if (!validateCustomization(root, errorEl)) throw new Error(errorEl?.textContent || 'Please check customization details.');

    const initial = customizationState(root);
    if (initial.addons.length) await resolveAddonIds(root);
    const state = customizationState(root);
    if (state.addons.some(addon => !Number(addon.id))) {
      throw new Error('Customization charges are unavailable. In Shopify Admin, keep the “Customization” product Active and published to Online Store.');
    }

    const bundleId = state.addons.length ? makeBundleId() : '';
    const baseProperties = { ...state.properties };
    if (bundleId) baseProperties._bundle_id = bundleId;

    const items = [{ id, quantity, properties: baseProperties }];
    const productTitle = root?.dataset.productTitle || product?.querySelector('h1')?.textContent?.trim() || 'DocScrubs item';
    state.addons.forEach(addon => {
      items.push({
        id: Number(addon.id),
        quantity,
        properties: {
          _bundle_id: bundleId,
          _addon_for: productTitle,
          _addon_label: addon.label
        }
      });
    });

    return addBundleItems(items);
  }

  window.DocScrubsAddProductForm = async function (form, options = {}) {
    const checkout = options.checkout === true;
    const trigger = options.trigger || form.querySelector('button[type="submit"]');
    return queueCart(async () => {
      await performProductAdd(form);
      announce(cfg.added);
      if (checkout) {
        location.assign(`${cfg.root}checkout`);
        return;
      }
      if (cfg.cartType === 'page') {
        location.assign(cfg.cart);
        return;
      }
      try {
        await refreshCart();
        if (!openDialog(document.getElementById('CartDrawer'), trigger)) location.assign(cfg.cart);
      } catch (_) {
        location.assign(cfg.cart);
      }
    });
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('[data-product-form]') || !window.fetch || event.defaultPrevented) return;
    event.preventDefault();
    if (form.dataset.pending === 'true') return;
    form.dataset.pending = 'true';
    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[data-form-error]') || form.closest('ds-product')?.querySelector('[data-variant-error]');
    const label = button?.textContent;
    if (error) error.hidden = true;
    if (button) { button.disabled = true; button.textContent = cfg.adding; }
    try {
      await window.DocScrubsAddProductForm(form, { trigger: button });
    } catch (err) {
      showError(error, err.message);
      announce(err.message);
    } finally {
      form.dataset.pending = 'false';
      if (button?.isConnected) { button.disabled = false; button.textContent = label; }
    }
  });
  document.addEventListener('click', async (event) => {
    const target = event.target;
    const close = target.closest('[data-dialog-close]');
    if (close) { closeDialog(close.closest('dialog')); return; }
    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen && cfg.cartType !== 'page') {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer?.showModal) return;
      event.preventDefault();
      openDialog(drawer, cartOpen);
      const error = drawer.querySelector('[data-cart-error]');
      if (error) error.hidden = true;
      try { await queueCart(refreshCart); } catch (err) { showError(error, err.message); }
      return;
    }
    const searchOpen = target.closest('[data-search-open]');
    if (searchOpen && document.getElementById('SearchDialog')?.showModal) { event.preventDefault(); openDialog(document.getElementById('SearchDialog'), searchOpen); document.getElementById('SearchDialogInput')?.focus(); return; }
    const clear = target.closest('[data-cart-clear]');
    if (clear) {
      event.preventDefault();
      await clearCart(clear.dataset.cartContext || 'drawer');
      return;
    }
    const removeAddon = target.closest('[data-cart-remove-addon]');
    if (removeAddon) {
      event.preventDefault();
      await removeAddonLine(
        removeAddon.dataset.lineKey,
        removeAddon.dataset.bundleId || '',
        removeAddon.dataset.addonLabel || '',
        removeAddon.dataset.cartContext || 'drawer'
      );
      return;
    }
    const remove = target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      if (remove.dataset.cartContext === 'page') await removePageCartLine(remove.dataset.lineKey, remove.dataset.bundleId || '');
      else await changeCart(remove.dataset.lineKey, 0, remove.dataset.bundleId || '');
      return;
    }
    const move = target.closest('[data-slider-prev],[data-slider-next]');
    if (move) { const track = move.closest('[data-slider]')?.querySelector('[data-slider-track]'); if (!track) return; const card = track.firstElementChild; const gap = parseFloat(getComputedStyle(track).gap) || 0; const amount = (card?.getBoundingClientRect().width || track.clientWidth) + gap; track.scrollBy({ left: amount * (move.matches('[data-slider-next]') ? 1 : -1), behavior: behavior() }); return; }
    const mediaButton = target.closest('[data-media-target]');
    if (mediaButton) {
      const gallery = mediaButton.closest('[data-gallery]');
      gallery.querySelectorAll('[data-media-id]').forEach(el => { el.hidden = el.dataset.mediaId !== mediaButton.dataset.mediaTarget; if (el.hidden) { el.querySelectorAll('video').forEach(video => video.pause()); el.querySelectorAll('iframe').forEach(frame => { const src = frame.src; frame.src = src; }); } });
      gallery.querySelectorAll('[data-media-target]').forEach(el => el.setAttribute('aria-pressed', String(el === mediaButton)));
      return;
    }
    if (target.tagName === 'DIALOG') { const box = target.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog(target); }
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]')) {
      const quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 0 || !input.checkValidity()) { input.reportValidity(); return; }
      changeCart(input.dataset.lineKey, quantity, input.dataset.bundleId || '');
    }
    if (input.matches('[data-page-cart-quantity]') && input.dataset.bundleId) {
      const quantity = Number(input.value);
      document.querySelectorAll('[data-page-addon-quantity]').forEach(addonInput => {
        if (addonInput.dataset.bundleId === input.dataset.bundleId) addonInput.value = String(quantity);
      });
    }
    const customRoot = input.closest?.('[data-addon-customization]');
    if (customRoot) updateCustomization(customRoot);
    if (input.matches('[data-auto-submit]')) input.form.requestSubmit();
    if (input.matches('[data-country]')) updateProvince(input);
  });

  document.addEventListener('input', event => {
    const root = event.target.closest?.('[data-addon-customization]');
    if (root) updateCustomization(root);
    if (event.target.matches('[data-product-quantity]')) {
      const custom = event.target.closest('ds-product')?.querySelector('[data-addon-customization]');
      if (custom) updateCustomization(custom);
    }
  });
  document.addEventListener('click', async event => {
    const target = event.target;
    const cardToggle = target.closest('[data-custom-card-toggle]');
    if (cardToggle) {
      const card = cardToggle.closest('[data-custom-card]');
      const body = card?.querySelector('.ds-custom-card-body');
      const open = !card?.classList.contains('is-open');
      card?.classList.toggle('is-open', open);
      cardToggle.setAttribute('aria-expanded', String(open));
      if (body) body.hidden = !open;
      return;
    }
    const trigger = target.closest('[data-qty-step]');
    if (trigger) {
      const input = document.getElementById(trigger.dataset.qtyTarget);
      if (!input) return;
      const step = Number(input.step || 1) || 1;
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const current = Number(input.value || min);
      const next = trigger.dataset.qtyStep === 'down' ? current - step : current + step;
      input.value = String(Math.min(max, Math.max(min, next)));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    const buyNow = target.closest('[data-buy-now]');
    if (buyNow) {
      const form = buyNow.closest('ds-product')?.querySelector('form[data-product-form]');
      if (!form || buyNow.disabled) return;
      const error = form.closest('ds-product')?.querySelector('[data-variant-error]');
      if (error) error.hidden = true;
      const label = buyNow.textContent;
      buyNow.disabled = true;
      buyNow.textContent = 'Processing…';
      try { await window.DocScrubsAddProductForm(form, { checkout: true, trigger: buyNow }); }
      catch (err) { showError(error, err.message); announce(err.message); buyNow.disabled = false; buyNow.textContent = label; }
      return;
    }
    const openSize = target.closest('[data-open-sizechart]');
    if (openSize) {
      event.preventDefault();
      const product = openSize.closest('ds-product');
      const modal = product?.nextElementSibling?.matches?.('.sizechart-modal-overlay') ? product.nextElementSibling : document.querySelector('.sizechart-modal-overlay');
      if (modal) { modal.hidden = false; modal.style.display = 'flex'; document.body.classList.add('sizechart-open'); }
      return;
    }
    const closeSize = target.closest('[data-close-sizechart]');
    if (closeSize) {
      const modal = closeSize.closest('.sizechart-modal-overlay');
      if (modal) { modal.hidden = true; modal.style.display = 'none'; document.body.classList.remove('sizechart-open'); }
      return;
    }
    if (target.matches('.sizechart-modal-overlay')) { target.hidden = true; target.style.display = 'none'; document.body.classList.remove('sizechart-open'); return; }
    const tab = target.closest('.product-tab-btn[data-tab]');
    if (tab) {
      const container = tab.closest('.product-tabs-container');
      if (!container) return;
      container.querySelectorAll('.product-tab-btn').forEach(btn => btn.classList.toggle('is-active', btn === tab));
      container.querySelectorAll('.product-tab-pane').forEach(pane => { pane.hidden = true; pane.classList.remove('is-active'); });
      const pane = container.querySelector(`[id^="Tab-${tab.dataset.tab}-"]`);
      if (pane) { pane.hidden = false; pane.classList.add('is-active'); }
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.site-header details[open]').forEach(el => { el.open = false; });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.desktop-nav details[open]').forEach(el => { if (!el.contains(event.target)) el.open = false; });
  });
  class DSProduct extends HTMLElement {
    connectedCallback() {
      this.controller = null;
      this.sequence = 0;
      this.onClick = event => {
        const link = event.target.closest('[data-option-link]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        const fallbackUrl = new URL(link.href, location.origin);
        if (fallbackUrl.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) return;
        event.preventDefault();
        this.selectVariant(link);
      };
      this.addEventListener('click', this.onClick);
      initProductExtras(this);
    }
    disconnectedCallback() { this.controller?.abort(); this.removeEventListener('click', this.onClick); }
    async selectVariant(link) {
      this.controller?.abort();
      this.controller = new AbortController();
      const sequence = ++this.sequence;
      const optionId = link.dataset.optionId;
      const clickedPosition = Number(link.dataset.optionPosition || 0);
      const selectedByPosition = new Map();
      this.querySelectorAll('[data-option-value-id][data-selected="true"]').forEach(el => {
        const position = Number(el.dataset.optionPosition || el.closest('[data-option-position]')?.dataset.optionPosition || 0);
        if (position) selectedByPosition.set(position, el.dataset.optionValueId);
      });
      if (clickedPosition && link.dataset.optionValueId) selectedByPosition.set(clickedPosition, link.dataset.optionValueId);
      const selectedIds = Array.from(selectedByPosition.entries()).sort((a, b) => a[0] - b[0]).map(([, id]) => id).filter(Boolean);
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.delete('variant');
      if (selectedIds.length) url.searchParams.set('option_values', selectedIds.join(','));
      const preserved = Array.from(this.querySelectorAll('[data-preserve-input]')).map(el => ({
        name: el.name,
        value: el.value,
        id: el.id || '',
        type: el.type || '',
        checked: !!el.checked
      }));
      const openAccordions = Array.from(this.querySelectorAll('.custom-accordion.is-open')).map(el => el.dataset.accordion).filter(Boolean);
      this.setAttribute('aria-busy', 'true');
      const sectionURL = new URL(url);
      sectionURL.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(sectionURL, { credentials: 'same-origin', signal: this.controller.signal });
        if (!response.ok) throw new Error(cfg.error);
        const html = await response.text();
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('ds-product');
        if (!next || sequence !== this.sequence) throw new Error(cfg.error);
        this.innerHTML = next.innerHTML;
        this.dataset.variantId = next.dataset.variantId || '';
        this.dataset.variantPrice = next.dataset.variantPrice || '0';
        this.dataset.variantCompareAtPrice = next.dataset.variantCompareAtPrice || '0';
        this.dataset.variantAvailable = next.dataset.variantAvailable || 'false';
        this.dataset.variantMedia = next.dataset.variantMedia || '';
        if (next.dataset.productUrl) this.dataset.productUrl = next.dataset.productUrl;
        preserved.forEach(({ name, value, id, type, checked }) => {
          const inputs = Array.from(this.querySelectorAll('[data-preserve-input]'));
          let el = null;
          if (type === 'radio' || type === 'checkbox') {
            el = inputs.find(input => input.name === name && input.value === value);
          } else if (id) {
            el = inputs.find(input => input.id === id);
          } else {
            el = inputs.find(input => input.name === name);
          }
          if (!el) return;
          if (type === 'radio' || type === 'checkbox') {
            el.checked = checked;
          } else if (el.tagName === 'SELECT') {
            if (Array.from(el.options).some(option => option.value === value)) el.value = value;
          } else if (el.type === 'number') {
            const min = Number(el.min) || 1, max = Number(el.max) || Infinity, step = Number(el.step) || 1;
            el.value = String(Math.min(max, min + Math.max(0, Math.round((Number(value) - min) / step)) * step));
          } else {
            el.value = value;
          }
        });
        openAccordions.forEach(key => {
          const accordion = this.querySelector(`.custom-accordion[data-accordion="${key}"]`);
          if (!accordion) return;
          accordion.classList.add('is-open');
          const header = accordion.querySelector('.custom-accordion-header');
          const body = accordion.querySelector('.custom-accordion-body');
          header?.setAttribute('aria-expanded', 'true');
          if (body) body.hidden = false;
        });
        history.replaceState({}, '', url);
        this.querySelector(`[data-option-id="${optionId}"]`)?.focus({ preventScroll: true });
        window.Shopify?.PaymentButton?.init();
        initProductExtras(this);
        announce(this.querySelector('.product-price')?.textContent.trim() || '');
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId: this.dataset.variantId, sectionId: this.dataset.sectionId } }));
      } catch (err) {
        if (err.name !== 'AbortError') { showError(this.querySelector('[data-variant-error]'), err.message); link.focus(); }
      } finally { if (sequence === this.sequence) this.setAttribute('aria-busy', 'false'); }
    }
  }
  class DSRecommendations extends HTMLElement {
    connectedCallback() {
      this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); } }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    async load() {
      try { const response = await fetch(this.dataset.url, { credentials: 'same-origin' }); if (!response.ok) return; const doc = new DOMParser().parseFromString(await response.text(), 'text/html'); const content = doc.querySelector('ds-recommendations'); if (content?.innerHTML.trim()) this.innerHTML = content.innerHTML; } catch (_) { /* Optional recommendations leave no empty frame on failure. */ }
    }
  }
  class DSAnnouncement extends HTMLElement {
    connectedCallback() {
      this.index = 0; this.paused = false;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.slides = Array.from(this.querySelectorAll('[data-ann-slide]'));
      this.querySelector('[data-ann-prev]')?.addEventListener('click', () => this.show(this.index - 1), { signal });
      this.querySelector('[data-ann-next]')?.addEventListener('click', () => this.show(this.index + 1), { signal });
      this.querySelector('[data-ann-pause]')?.addEventListener('click', event => { this.paused = !this.paused; event.currentTarget.setAttribute('aria-pressed', String(this.paused)); this.paused ? this.stop() : this.start(); }, { signal });
      this.addEventListener('mouseenter', () => this.stop(), { signal });
      this.addEventListener('mouseleave', () => this.start(), { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', () => this.start(), { signal });
      document.addEventListener('shopify:block:select', event => { const index = this.slides.findIndex(slide => slide.contains(event.target)); if (index >= 0) { this.stop(); this.show(index); } }, { signal });
      this.start();
    }
    show(index) { if (!this.slides.length) return; this.index = (index + this.slides.length) % this.slides.length; this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; }); }
    start() { this.stop(); if (this.dataset.autoplay !== 'true' || this.paused || reduced.matches || window.Shopify?.designMode || this.slides.length < 2) return; this.timer = setInterval(() => this.show(this.index + 1), Number(this.dataset.interval) || 5000); }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { this.stop(); this.events?.abort(); }
  }
  customElements.define('ds-product', DSProduct);
  customElements.define('ds-recommendations', DSRecommendations);
  customElements.define('ds-announcement', DSAnnouncement);
  function updateProvince(country) {
    const fields = country.closest('[data-address-fields]');
    const province = fields.querySelector('[data-province]');
    let options = [];
    try { options = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) { /* No provinces. */ }
    province.replaceChildren(...options.map(([value, label]) => new Option(label, value)));
    fields.querySelector('[data-province-wrap]').hidden = !options.length;
    province.disabled = !options.length;
    if (province.dataset.default) { province.value = province.dataset.default; delete province.dataset.default; }
  }
  let revealObserver;
  function init(root = document) {
    root.querySelectorAll('[data-country]').forEach(country => { if (country.dataset.initialized) return; country.dataset.initialized = 'true'; if (country.dataset.default) country.value = country.dataset.default; updateProvince(country); });
    if (!window.Shopify?.designMode && !reduced.matches && document.body.classList.contains('animations')) {
      revealObserver ||= new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); } }), { rootMargin: '40px', threshold: 0.04 });
      root.querySelectorAll('.section-shell:not(.hero):not(.product-section)').forEach(el => { if (!el.classList.contains('reveal')) { el.classList.add('reveal'); revealObserver.observe(el); } });
    }
  }

  function initProductExtras(root = document) {
    root.querySelectorAll('[data-live-viewers]').forEach(el => {
      if (el.dataset.initialized === 'true') return;
      el.dataset.initialized = 'true';
      const min = Number(el.dataset.min || 1), max = Number(el.dataset.max || min);
      const target = el.querySelector('[data-live-viewers-count]');
      const randomCount = () => Math.max(min, Math.round(Math.random() * (Math.max(max, min) - min)) + min);
      if (target) target.textContent = randomCount();
      const tick = () => { if (target) target.textContent = randomCount(); };
      const delay = () => 3500 + Math.round(Math.random() * 4500);
      let timer;
      const schedule = () => { clearTimeout(timer); timer = setTimeout(() => { tick(); schedule(); }, delay()); };
      schedule();
    });
    root.querySelectorAll('[data-addon-customization]').forEach(updateCustomization);
  }

  init();
  initProductExtras();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:select', event => event.target.querySelectorAll('.reveal').forEach(el => el.classList.add('is-visible')));
  document.addEventListener('shopify:block:select', event => { const track = event.target.closest('[data-slider-track]'); if (track) event.target.scrollIntoView({ behavior: behavior(), block: 'nearest', inline: 'center' }); });
})();
